import Papa from "papaparse"
import { formatField, shouldExcludeExportField } from "@/stores/chat/helpers.js"

const EXCEL_CURRENCY_STYLE = 1
const EXCEL_DATE_STYLE = 2
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30)

export { shouldExcludeExportField }

const CRC_TABLE = (function buildCrcTable () {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) {
      c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1)
    }
    table[i] = c >>> 0
  }
  return table
})()

export function collectExportColumns (rows) {
  const columns = []
  const seen = {}
  if (!Array.isArray(rows)) return columns
  rows.forEach(function (row) {
    if (!Array.isArray(row)) return
    row.forEach(function (field) {
      const names = Array.isArray(field.field_name) ? field.field_name : [field.field_name]
      names.forEach(function (name) {
        if (shouldExcludeExportField(name) || seen[name]) return
        seen[name] = true
        columns.push({
          name: name,
          displayName: field.display_name || name,
          dataType: field.data_type,
          symbol: field.symbol,
          format: field.format
        })
      })
    })
  })
  return columns
}

function findField (row, headerName) {
  if (!Array.isArray(row)) return null
  for (let i = 0; i < row.length; i++) {
    const field = row[i]
    const fn = Array.isArray(field.field_name) ? field.field_name[0] : field.field_name
    if (fn === headerName) return field
  }
  return null
}

function numericValue (value) {
  if (value === null || value === undefined || value === "") return null
  if (typeof value === "number") {
    return isNaN(value) ? null : value
  }
  const parsed = parseFloat(String(value).replace(/,/g, "").replace(/^[£$€]\s*/, ""))
  return isNaN(parsed) ? null : parsed
}

function parseDateValue (value) {
  if (value === null || value === undefined || value === "") return null
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value
  }
  const parsed = new Date(value)
  return isNaN(parsed.getTime()) ? null : parsed
}

function excelSerialFromDate (date) {
  return (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - EXCEL_EPOCH_MS) / 86400000
}

export function exportCellValue (field) {
  if (!field || field.value === null || field.value === undefined) return ""
  if (field.data_type === "currency" || field.data_type === "number" || field.data_type === "integer" || field.data_type === "float") {
    const num = numericValue(field.value)
    return num === null ? "" : num
  }
  if (field.data_type === "days" || field.data_type === "date_span") {
    const num = parseFloat(field.value)
    if (isNaN(num)) return ""
    return Math.ceil(num)
  }
  return formatField(field)
}

export function buildExportMatrix (rows) {
  const columns = collectExportColumns(rows)
  const headers = columns.map(function (column) { return column.displayName })
  const body = []
  const cellMeta = []
  if (!Array.isArray(rows)) {
    return { headers: headers, body: body, columns: columns, cellMeta: cellMeta }
  }
  rows.forEach(function (row) {
    const values = []
    const meta = []
    columns.forEach(function (column) {
      const field = findField(row, column.name) || {
        field_name: column.name,
        value: "",
        data_type: column.dataType,
        symbol: column.symbol,
        format: column.format,
        display_name: column.displayName
      }
      values.push(exportCellValue(field))
      meta.push({
        dataType: field.data_type || column.dataType,
        symbol: field.symbol || column.symbol || "£",
        rawValue: field.value
      })
    })
    body.push(values)
    cellMeta.push(meta)
  })
  return { headers: headers, body: body, columns: columns, cellMeta: cellMeta }
}

export function matrixToCsv (matrix, delimiter) {
  const sep = delimiter || ","
  return Papa.unparse([matrix.headers].concat(matrix.body), { delimiter: sep })
}

export function buildCsvBlob (rows) {
  const csv = matrixToCsv(buildExportMatrix(rows), ",")
  return new Blob([csv], { type: "text/csv;charset=utf-8;" })
}

export function buildTsvText (rows) {
  return matrixToCsv(buildExportMatrix(rows), "\t")
}

function xmlEscape (value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

function columnLetter (index) {
  let n = index + 1
  let letters = ""
  while (n > 0) {
    const rem = (n - 1) % 26
    letters = String.fromCharCode(65 + rem) + letters
    n = Math.floor((n - 1) / 26)
  }
  return letters
}

function crc32 (bytes) {
  let crc = 0xFFFFFFFF
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8)
  }
  return (crc ^ 0xFFFFFFFF) >>> 0
}

function u16 (n) {
  const b = new Uint8Array(2)
  new DataView(b.buffer).setUint16(0, n, true)
  return b
}

function u32 (n) {
  const b = new Uint8Array(4)
  new DataView(b.buffer).setUint32(0, n, true)
  return b
}

function concatBytes (chunks) {
  let total = 0
  for (let i = 0; i < chunks.length; i++) total += chunks[i].length
  const out = new Uint8Array(total)
  let offset = 0
  for (let i = 0; i < chunks.length; i++) {
    out.set(chunks[i], offset)
    offset += chunks[i].length
  }
  return out
}

function zipUncompressed (files) {
  const localParts = []
  const centralParts = []
  let offset = 0
  files.forEach(function (file) {
    const nameBytes = new TextEncoder().encode(file.name)
    const data = typeof file.data === "string" ? new TextEncoder().encode(file.data) : file.data
    const crc = crc32(data)
    const local = concatBytes([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
      data
    ])
    localParts.push(local)
    centralParts.push(concatBytes([
      u32(0x02014b50),
      u16(20),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      nameBytes
    ]))
    offset += local.length
  })
  const central = concatBytes(centralParts)
  const end = concatBytes([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(central.length),
    u32(offset),
    u16(0)
  ])
  return concatBytes(localParts.concat([central, end]))
}

function excelCellXml (rowIndex, colIndex, value, meta) {
  const ref = columnLetter(colIndex) + String(rowIndex)
  const dataType = meta && meta.dataType
  if (dataType === "currency" || dataType === "number" || dataType === "integer" || dataType === "float") {
    const num = numericValue(meta && meta.rawValue !== undefined ? meta.rawValue : value)
    if (num !== null) {
      const style = dataType === "currency" ? EXCEL_CURRENCY_STYLE : 0
      const styleAttr = style ? ' s="' + style + '"' : ""
      return '<c r="' + ref + '" t="n"' + styleAttr + "><v>" + num + "</v></c>"
    }
  }
  if (dataType === "days" || dataType === "date_span") {
    const num = typeof value === "number" ? value : numericValue(value)
    if (num !== null) {
      return '<c r="' + ref + '" t="n"><v>' + num + "</v></c>"
    }
  }
  if (dataType === "date" || dataType === "datetime") {
    const parsed = parseDateValue(meta && meta.rawValue)
    if (parsed) {
      return '<c r="' + ref + '" t="n" s="' + EXCEL_DATE_STYLE + '"><v>' + excelSerialFromDate(parsed) + "</v></c>"
    }
  }
  if (typeof value === "number" && !isNaN(value)) {
    return '<c r="' + ref + '" t="n"><v>' + value + "</v></c>"
  }
  if (value === "" || value === null || value === undefined) {
    return '<c r="' + ref + '"/>'
  }
  return '<c r="' + ref + '" t="inlineStr"><is><t>' + xmlEscape(value) + "</t></is></c>"
}

function buildSheetXml (matrix) {
  const rows = []
  const headerCells = matrix.headers.map(function (header, colIndex) {
    return '<c r="' + columnLetter(colIndex) + '1" t="inlineStr"><is><t>' + xmlEscape(header) + "</t></is></c>"
  }).join("")
  rows.push('<row r="1">' + headerCells + "</row>")
  matrix.body.forEach(function (row, rowOffset) {
    const rowIndex = rowOffset + 2
    const cells = row.map(function (value, colIndex) {
      const meta = matrix.cellMeta[rowOffset] && matrix.cellMeta[rowOffset][colIndex]
      return excelCellXml(rowIndex, colIndex, value, meta)
    }).join("")
    rows.push('<row r="' + rowIndex + '">' + cells + "</row>")
  })
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    "<sheetData>" + rows.join("") + "</sheetData></worksheet>"
}

function buildStylesXml () {
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    '<numFmts count="2">' +
    '<numFmt numFmtId="164" formatCode="&quot;£&quot;#,##0.00"/>' +
    '<numFmt numFmtId="165" formatCode="DD/MM/YYYY"/>' +
    "</numFmts>" +
    '<fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts>' +
    '<fills count="1"><fill><patternFill patternType="none"/></fill></fills>' +
    "<borders count=\"1\"><border/></borders>" +
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
    '<cellXfs count="3">' +
    '<xf numFmtId="0" fontId="0" fillId="0" borderId="0"/>' +
    '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" applyNumberFormat="1"/>' +
    '<xf numFmtId="165" fontId="0" fillId="0" borderId="0" applyNumberFormat="1"/>' +
    "</cellXfs></styleSheet>"
}

export function buildExcelBytes (rows) {
  const matrix = buildExportMatrix(rows)
  return zipUncompressed([
    {
      name: "[Content_Types].xml",
      data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        "</Types>"
    },
    {
      name: "_rels/.rels",
      data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
        "</Relationships>"
    },
    {
      name: "xl/workbook.xml",
      data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
        "<sheets><sheet name=\"Report\" sheetId=\"1\" r:id=\"rId1\"/></sheets></workbook>"
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
        '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
        "</Relationships>"
    },
    { name: "xl/styles.xml", data: buildStylesXml() },
    { name: "xl/worksheets/sheet1.xml", data: buildSheetXml(matrix) }
  ])
}

export function buildExcelBlob (rows) {
  return new Blob([buildExcelBytes(rows)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  })
}
